import React, { useState, useEffect } from "react";
import SideBar from "../inc/SideBar";
import NavBar from "../inc/NavBar";
import FilterSidebar from "../inc/FilterSidebar";
import { Container, Row, Col, Dropdown, Form } from "react-bootstrap";
import { FiChevronRight, FiSearch, FiMoreVertical } from "react-icons/fi";
import { FiPlus } from "react-icons/fi";
import AxiosConfig from "../services/AxiosConfig";
import PlacesAutocomplete from "react-places-autocomplete";
import { country_PhoneNumberCode } from "../services/ApiCalls";
import TextInputWithLabel from "../customcomponents/TextInputWithLabel";
import PillSelect, { LABEL_OPTIONS } from "../customcomponents/PillSelect";
import "./Settings.scss";

const Settings = () => {
  const [toggle, setToggle] = useState(false);
  const [view, setView] = useState("dashboard"); // dashboard, contactsList, newContact
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingContactId, setEditingContactId] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [phoneNumberCode, setPhoneNumberCode] = useState([]);
  const [saveError, setSaveError] = useState("");
  const [isGoogleReady, setIsGoogleReady] = useState(
    () => Boolean(window.google?.maps?.places)
  );

  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const currentOwnerId = userDetail._id || "";
  const currentOwnerRole = userDetail.profile || "";

  const getDefaultContactPerson = () => {
    return {
      fullName: "",
      // Empty value + placeholder example (same pattern as Quotes) — do not autofill profile phone
      phone: "",
      email: userDetail.email || userDetail.contactEmail || "",
      phoneCode: "+33",
      label: "Delivery point",
    };
  };

  const getEmptyFormData = () => ({
    companyName: "",
    vatNumber: "",
    addresses: [{ address: "", label: "Delivery point" }],
    contactPersons: [getDefaultContactPerson()],
  });

  const [formData, setFormData] = useState(getEmptyFormData());

  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsGoogleReady(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );

    const markReady = () => {
      if (window.google?.maps?.places) {
        setIsGoogleReady(true);
        return true;
      }
      return false;
    };

    if (existingScript) {
      const interval = setInterval(() => {
        if (markReady()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }

    const script = document.createElement("script");
    script.src =
      "https://maps.googleapis.com/maps/api/js?key=AIzaSyB3cLetAkii900cvYPIrvxILEJ6qUhpG4I&libraries=places";
    script.async = true;
    script.onload = () => markReady();
    document.body.appendChild(script);

    const interval = setInterval(() => {
      if (markReady()) clearInterval(interval);
    }, 200);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (toggle) {
      document.body.classList.add("filter_sidebar");
    } else {
      document.body.classList.remove("filter_sidebar");
    }
  }, [toggle]);

  const fetchContacts = async () => {
    try {
      if (!currentOwnerId) {
        setContacts([]);
        return;
      }
      const response = await AxiosConfig.instance.get("/get_contacts", {
        params: { ownerId: currentOwnerId },
      });
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching contacts", err);
    }
  };

  useEffect(() => {
    if (view === "contactsList") {
      fetchContacts();
    }
  }, [view]);

  useEffect(() => {
    country_PhoneNumberCode({}).then((res) => {
      if (res && res.data) {
        setPhoneNumberCode(res.data);
      }
    }).catch(err => console.error("Error fetching phone codes", err));
  }, []);

  const handleToggle = () => {
    setToggle(!toggle);
  };

  const handleAddAddress = () => {
    setFormData({
      ...formData,
      addresses: [...formData.addresses, { address: "", label: "Delivery point" }]
    });
  };

  const handleAddressChange = (index, field, value) => {
    const updated = [...formData.addresses];
    updated[index][field] = value;
    setFormData({ ...formData, addresses: updated });
  };

  const handleAddContactPerson = () => {
    setFormData({
      ...formData,
      contactPersons: [...formData.contactPersons, getDefaultContactPerson()],
    });
  };

  const handleContactPersonChange = (index, field, value) => {
    const updated = [...formData.contactPersons];
    updated[index][field] = value;
    setFormData({ ...formData, contactPersons: updated });
  };

  const populateContactForm = (contact) => ({
    companyName: contact.companyName || "",
    vatNumber: contact.vatNumber || "",
    addresses:
      contact.addresses && contact.addresses.length
        ? contact.addresses
        : [{ address: "", label: "Delivery point" }],
    contactPersons:
      contact.contactPersons && contact.contactPersons.length
        ? contact.contactPersons.map((person) => ({
            ...person,
            phoneCode: person.phoneCode || "+33",
            label: person.label || "Delivery point",
          }))
        : [getDefaultContactPerson()],
  });

  const handleViewContact = (contact) => {
    setEditingContactId(contact._id);
    setIsViewOnly(true);
    setFormData(populateContactForm(contact));
    setView("newContact");
  };

  const handleEditContact = (contact) => {
    setEditingContactId(contact._id);
    setIsViewOnly(false);
    setFormData(populateContactForm(contact));
    setView("newContact");
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      const response = await AxiosConfig.instance.post(`/delete_contact/${id}`, {
        ownerId: currentOwnerId,
      });
      if (response.data.success) {
        fetchContacts();
      }
    } catch (err) {
      console.error("Error deleting contact", err);
    }
  };

  const handleSaveContact = async () => {
    if (isViewOnly) return;
    setSaveError("");
    try {
      let response;
      const payload = {
        ...formData,
        ownerId: currentOwnerId,
        ownerRole: currentOwnerRole,
      };
      if (editingContactId) {
        response = await AxiosConfig.instance.post(
          `/update_contact/${editingContactId}`,
          payload
        );
      } else {
        response = await AxiosConfig.instance.post("/add_contact", payload);
      }

      if (response.data.success) {
        setView("contactsList");
        setFormData(getEmptyFormData());
        setEditingContactId(null);
        setIsViewOnly(false);
        fetchContacts();
      } else {
        setSaveError(response.data.message || "Failed to save contact.");
      }
    } catch (err) {
      console.error("Error saving contact", err);
      if (err.response && err.response.data && err.response.data.message) {
        setSaveError(err.response.data.message);
      } else {
        setSaveError(
          "Failed to save contact. Please check that all fields are filled."
        );
      }
    }
  };

  const renderDashboard = () => (
    <div className="dashboard-grid">
      <div className="setting-card" onClick={() => setView("contactsList")}>
        <h3>Contacts</h3>
        <FiChevronRight />
      </div>
      {/* Les autres cartes vides pour matcher la maquette */}
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
      <div className="setting-card" style={{ opacity: 0.5, cursor: "default" }}></div>
    </div>
  );

  const renderContactsList = () => {
    const filtered = contacts.filter((c) =>
      c.companyName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="list-view">
        <div className="table-actions">
          <button className="new-btn" onClick={() => {
            setEditingContactId(null);
            setIsViewOnly(false);
            setFormData(getEmptyFormData());
            setView("newContact");
          }}>
            <FiPlus /> New Contact
          </button>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: "50px" }}></th>
                <th>COMPANY NAME</th>
                <th>ADDRESSES</th>
                <th>CONTACT</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((contact, index) => (
                <tr key={contact._id}>
                  <td style={{ color: "#a4b0be" }}>{index + 1}</td>
                  <td>{contact.companyName}</td>
                  <td style={{ color: "#4f5b92" }}>{String(contact.addresses.length).padStart(2, '0')}</td>
                  <td style={{ color: "#4f5b92" }}>{String(contact.contactPersons.length).padStart(2, '0')}</td>
                  <td>
                    <Dropdown align="end" className="action-dropdown">
                      <Dropdown.Toggle
                        variant="link"
                        id={`contact-actions-${contact._id}`}
                        className="contacts-row-actions"
                      >
                        <FiMoreVertical size={18} color="#4f5b92" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleViewContact(contact)}>View</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleEditContact(contact)}>Edit</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDeleteContact(contact._id)} className="text-danger">Delete</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    No contacts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderNewContact = () => (
    <div className={`form-container${isViewOnly ? " view-only" : ""}`}>
      <div className="section-title">
        {isViewOnly ? "VIEW CONTACT" : "COMPANY INFORMATION"}
      </div>
      <Row>
        <Col md={6}>
          <div className="form-group">
            <label>Company name</label>
            <input
              type="text"
              placeholder="Enter company name"
              value={formData.companyName}
              disabled={isViewOnly}
              readOnly={isViewOnly}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            />
          </div>
        </Col>
        <Col md={6}>
          <div className="form-group">
            <label>VAT number</label>
            <input
              type="text"
              placeholder="Enter VAT number"
              value={formData.vatNumber}
              disabled={isViewOnly}
              readOnly={isViewOnly}
              onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
            />
          </div>
        </Col>
      </Row>

      <div className="section-title">ADDRESSES</div>
      {formData.addresses.map((addr, index) => (
        <Row key={`addr-${index}`}>
          <Col md={9}>
            <div className="form-group">
              <label>Address</label>
              {isViewOnly || !isGoogleReady ? (
                <input
                  type="text"
                  placeholder={isGoogleReady ? "" : "Loading address autocomplete..."}
                  value={addr.address}
                  readOnly={isViewOnly}
                  disabled={isViewOnly}
                  onChange={(e) =>
                    handleAddressChange(index, "address", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "12px",
                    border: "1px solid #e1e5ee",
                  }}
                />
              ) : (
                <PlacesAutocomplete value={addr.address} onChange={(val) => handleAddressChange(index, "address", val)} onSelect={(val) => handleAddressChange(index, "address", val)}>
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                      <input {...getInputProps({ placeholder: "Enter address", className: "location-search-input" })} style={{ width: "100%", padding: "10px", borderRadius: "12px", border: "1px solid #e1e5ee" }} />
                      <div className="autocomplete-dropdown-container" style={{ position: "absolute", zIndex: 1000, width: "90%", backgroundColor: "white", borderRadius: "8px", boxShadow: "0px 4px 10px rgba(0,0,0,0.1)" }}>
                        {loading && <div style={{padding: "10px"}}>Loading...</div>}
                        {suggestions.map((suggestion, i) => {
                          const style = suggestion.active ? { backgroundColor: "#f8f9fa", cursor: "pointer", padding: "10px" } : { backgroundColor: "#ffffff", cursor: "pointer", padding: "10px" };
                          return (
                            <div key={i} {...getSuggestionItemProps(suggestion, { style })}>
                              <span>{suggestion.description}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </PlacesAutocomplete>
              )}
            </div>
          </Col>
          <Col md={3}>
            <PillSelect
              label="Address label"
              name={`address-label-${index}`}
              value={addr.label}
              onChange={(e) => handleAddressChange(index, "label", e.target.value)}
              options={LABEL_OPTIONS}
              disabled={isViewOnly}
            />
          </Col>
        </Row>
      ))}
      {!isViewOnly && (
        <div className="add-btn-container">
          <button onClick={handleAddAddress}><FiPlus /> Add Address</button>
        </div>
      )}

      <div className="section-title">CONTACT PERSON</div>
      {formData.contactPersons.map((person, index) => (
        <Row key={`person-${index}`}>
          <Col md={3}>
            <div className="form-group">
              <label>Full name</label>
              <input
                type="text"
                placeholder="Enter full name"
                value={person.fullName}
                disabled={isViewOnly}
                readOnly={isViewOnly}
                onChange={(e) => handleContactPersonChange(index, "fullName", e.target.value)}
              />
            </div>
          </Col>
          <Col md={3}>
            <div className="counCode countFLEX">
              <Form.Select
                size="lg"
                value={person.phoneCode || "+33"}
                disabled={isViewOnly}
                onChange={(e) => handleContactPersonChange(index, "phoneCode", e.target.value)}
              >
                {phoneNumberCode.map((option, i) => (
                  <option key={i} value={option.code}>
                    {option.abbreviation} ({option.code})
                  </option>
                ))}
              </Form.Select>
              <TextInputWithLabel
                label="Contact phone"
                placeholder="Enter a phone number"
                type="text"
                reqstart="*"
                value={person.phone}
                disabled={isViewOnly}
                onChange={(e) => handleContactPersonChange(index, "phone", e.target.value)}
              />
            </div>
          </Col>
          <Col md={4}>
            <TextInputWithLabel
              label="Contact email"
              placeholder="Enter an email"
              type="email"
              reqstart="*"
              value={person.email}
              disabled={isViewOnly}
              onChange={(e) => handleContactPersonChange(index, "email", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <PillSelect
              label="Contact label"
              name={`contact-label-${index}`}
              value={person.label}
              onChange={(e) => handleContactPersonChange(index, "label", e.target.value)}
              options={LABEL_OPTIONS}
              disabled={isViewOnly}
            />
          </Col>
        </Row>
      ))}
      {!isViewOnly && (
        <div className="add-btn-container">
          <button onClick={handleAddContactPerson}><FiPlus /> Add Contact</button>
        </div>
      )}

      {saveError && <div style={{ color: "red", marginBottom: "15px", fontFamily: "AvenirLTProLight", textAlign: "center" }}>{saveError}</div>}
      <div className="form-actions">
        {!isViewOnly && (
          <button className="save-btn" onClick={handleSaveContact}>Save information</button>
        )}
        {!isViewOnly && editingContactId && (
          <button className="outline-btn" onClick={() => handleViewContact({ ...formData, _id: editingContactId })}>View</button>
        )}
        {isViewOnly && editingContactId && (
          <button
            className="save-btn"
            onClick={() => {
              setIsViewOnly(false);
            }}
          >
            Edit
          </button>
        )}
        <button
          className="outline-btn"
          onClick={() => {
            setIsViewOnly(false);
            setEditingContactId(null);
            setView("contactsList");
          }}
        >
          {isViewOnly ? "Close" : "Cancel"}
        </button>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="d-flex" id="wrapper">
        <SideBar />
        <div id="page-content-wrapper">
          <NavBar />
          <FilterSidebar close={handleToggle} />

          <Container fluid className="settings-page">
            {view === "dashboard" && renderDashboard()}
            {view === "contactsList" && renderContactsList()}
            {view === "newContact" && renderNewContact()}
          </Container>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Settings;
